(ns shared.core.components.tree
  (:require [shared.core.components.a :as a]
            [shared.core.components.interactive :refer [interactive]]
            [shared.core.components.menu :refer [menu]]
            [shared.core.components.icon :as icon]
            [shared.core.components.checkbox :refer [checkbox]]
            [shared.core.components.radio :refer [radio]]
            [shared.core.loading :as loading]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.components.theme :as theme]
            [shared.core.components.tooltip :refer [tooltip]]
            [shared.core.components.pill :refer [click-to-copy-pill]]
            [shared.core.util :as util]))

(defn large-tree-action [{:keys [color dark-color icon-type on-click style dark-style hover-style dark-hover-style
                                 loading? disabled?]}]
  [theme/theme interactive :div {:style (merge {:background-color (style/color :white :base)
                                                :border-left (str "1px solid " (style/color :gray :shade-3))
                                                :color color
                                                :cursor (if (or loading? disabled?)
                                                          :default
                                                          :pointer)
                                                :display :flex
                                                :align-items :center
                                                :justify-content :center
                                                :transition "0.2s"
                                                :height "100%"
                                                :width 60}
                                               (when disabled?
                                                 {:opacity 0.5})
                                               style)
                                 :theme (style/dark-style (merge {:background-color (style/color :surface :base)
                                                                  :color (when dark-color dark-color)}
                                                                 dark-style))
                                 :hover-style (when (and (not loading?) (not disabled?))
                                                (merge {:background-color color
                                                        :border-left (str "1px solid " color)
                                                        :color (style/color :white :base)}
                                                       hover-style))
                                 :dark-hover-style (when (and (not loading?) (not disabled?))
                                                     (merge {:background-color dark-color}
                                                            dark-hover-style))
                                 :on-click (when (and (not loading?) (not disabled?)) on-click)}
   (if loading?
     [loading/spinner {:color (style/color :green :base)
                       :size 18}]
     [icon/material-icon {:icon-type icon-type
                          :style {:color :inherit}}])])

(defn tree-action [{:keys [icon-type icon-style hover-style dark-hover-style on-click description style dark-style options
                           on-option-click parent-hover?* hover?* disabled? options-container-style force-show-description? size
                           show-on-click?]
                    :as args}]
  (let [options (some->> options (filter identity))
        options? (some-> options (count) (pos?))]
    ^{:key (pr-str (dissoc args :hover?* :parent-hover?*))}
    [tooltip
     {:placement :bottom
      :tooltip-content (when (or (not disabled?) force-show-description?)
                         (if options?
                           [menu {:options (map #(assoc % :on-click (when (:on-click %)
                                                                      (fn [i]
                                                                        (re/dispatch [:tippy/hide-all])
                                                                        ((:on-click %) i))))
                                                options)
                                  :on-item-click (when-not disabled?
                                                   #(do
                                                      (when hover?*
                                                        (reset! hover?* false))
                                                      (when parent-hover?*
                                                        (reset! parent-hover?* false))
                                                      (re/dispatch [:tippy/hide-all])
                                                      (on-option-click %)))
                                  :hover?* parent-hover?*}]
                           (when description
                             [interactive :span {:style {:color (style/color :white :base)}
                                                 :hover?* hover?*}
                              description])))
      :tooltip-container-style (when options?
                                 (merge {:padding 0
                                         :width 130}
                                        options-container-style))
      :interactive? options?
      :show-on-click? show-on-click?
      :append-to js/document.body
      :hover?* hover?*
      :target-content [theme/theme
                       interactive :div {:style (merge {:background-color :transparent
                                                        :border-radius "50%"
                                                        :color (style/color :gray :shade-1)
                                                        :cursor :pointer
                                                        :display :flex
                                                        :align-items :center
                                                        :justify-content :center
                                                        :margin "0 3px"
                                                        :transition "0.2s"
                                                        :height 36
                                                        :width 36}
                                                       (when (= size :medium)
                                                         {:height 25
                                                          :width 25})
                                                       (when disabled?
                                                         {:cursor :default
                                                          :opacity 0.6})
                                                       style)
                                         :hover-style (when-not disabled?
                                                        (merge {:background-color (style/color :gray :shade-2)
                                                                :color (style/color :gray :base)}
                                                               hover-style))
                                         :theme (style/dark-prop
                                                  {:style (merge {:color (style/dark-color :on-surface :base)}
                                                                 dark-style)
                                                   :hover-style (when-not disabled?
                                                                  (merge {:background-color (style/dark-color :on-surface :base)
                                                                          :color (style/dark-color :surface :base)}
                                                                         dark-hover-style))})
                                         :on-click (when-not disabled?
                                                     #(do
                                                        (when on-click
                                                          (on-click %))
                                                        ;; For click-triggered tooltips, let the click reach the
                                                        ;; tooltip reference element so tippy can toggle the menu.
                                                        ;; The reference itself stops propagation to the tree row.
                                                        (when-not show-on-click?
                                                          (util/stop-event %))))}
                       [icon/material-icon-tooltip {:icon-type icon-type
                                                    :style (merge {:color :inherit
                                                                   :font-size 20}
                                                                  (when (= size :medium)
                                                                    {:font-size 16})
                                                                  icon-style)
                                                    :tooltip-content (when show-on-click?
                                                                       description)}]]}]))

(defn standard-tree-contents [{:keys [label content content-style click-to-copy-pills] :as args}]
  ^{:key (pr-str args)}
  [:div {:style {:display :flex
                 :flex-direction :column
                 :position :relative
                 :width "100%"}}
   (when label
     [:div {:style {:display :flex
                    :align-items :center
                    :margin-bottom 6
                    :height 17}}
      [theme/theme
       :div {:style {:color (style/color :gray :shade-1)
                     :font-size 12
                     :font-weight style/font-extra-bold-weight
                     :letter-spacing "1px"
                     :text-transform :uppercase
                     :margin-right 6}
             :theme (style/dark-style {:color (style/dark-color :on-surface :base)})}
       (util/->capitalize-words label)]])
   [:div {:style {:display :flex
                  :align-items :center}}
    (into [theme/theme
           :span {:style (merge {:color (style/color :gray :base)}
                                content-style)
                  :theme (style/dark-style {:color (style/dark-color :on-surface :base)})}]
          [content])]
   (into [:div {:style {:display :flex
                        :align-items :center}}]
         (map (fn [p]
                [click-to-copy-pill p])
              (filter identity click-to-copy-pills)))])

(defn tree-item [{:keys [actions]}]
  (let [main-hover?* (re/ratom false)
        action-hovers* (re/ratom (repeat (count actions) (re/ratom false)))]
    (re/create-class
      {:component-did-update
       (fn [this]
         (let [props (re/props this)]
           (when (not= (count (:actions props))
                       (count @action-hovers*))
             (reset! action-hovers* (repeat (count (:actions props)) (re/ratom false))))))
       :reagent-render
       (fn [{:keys [checkable? checked? expandable? expanded? children on-click on-check style inner-style content level
                    actions force-show-actions? hover-style child-cont-border-color child-cont-bg-color on-mouse-over
                    on-mouse-out checkbox-container-style dark-checkbox-container-style checkbox-container-hover-style
                    dark-checkbox-container-hover-style checkbox-style dark-checkbox-style check-disabled? dark-style
                    size show-actions-toggled?]
             :as item}]
         (let [children? (some-> children (count) (pos?))
               child? (pos? level)
               action-hover? (some (fn [r?*] @r?*) @action-hovers*)
               force-show-actions? (and force-show-actions? show-actions-toggled?)
               hover-style? (or on-click on-check)]
           [theme/theme
            :div {:style (merge {:background-color (style/color :white :base)
                                 :border (if child?
                                           :none
                                           (str "1px solid " (style/color :gray :shade-3)))
                                 :cursor (if (or (and children? expandable?) on-click) :pointer :default)
                                 :transition "0.2s"}
                                (when-not child?
                                  {:border-radius 3})
                                (when child?
                                  {:border-top-left-radius 3
                                   :border-bottom-left-radius 3})
                                style)
                  :theme (style/dark-style (merge {:background-color (style/dark-color :surface :base)
                                                   :border (if child?
                                                             :none
                                                             (str "1px solid " (style/dark-color :surface :lighter-2)))}
                                                  dark-style))
                  :on-click #(do
                               (when (and (not action-hover?) on-click)
                                 (on-click item))
                               (util/stop-event %))}
            [:div {:style (merge {:border-top-left-radius 3
                                  :flex 1
                                  :display :flex
                                  :position :relative}
                                 (when-not children?
                                   {:border-bottom-left-radius 3})
                                 inner-style)}
             [theme/theme
              interactive :div {:style {:flex 1
                                        :display :flex
                                        :align-items :center
                                        :transition "background-color 0.2s"}
                                :on-mouse-over on-mouse-over
                                :on-mouse-out on-mouse-out
                                :hover-style (when hover-style?
                                               (merge {:background-color (style/color :gray :shade-11)}
                                                      hover-style))
                                :theme (style/dark-prop
                                         {:hover-style (when hover-style?
                                                         {:background-color (style/dark-color :surface :lighter-1)})})
                                :hover?* main-hover?*}
              (when (and checkable? on-check)
                [theme/theme
                 interactive :div {:style (merge {:background-color (if checked?
                                                                      (style/color :green :shade-3)
                                                                      (style/color :white :base))
                                                  :border-right (str "1px solid " (style/color :gray :shade-3))
                                                  :cursor :pointer
                                                  :flex "0 0 auto"
                                                  :display :flex
                                                  :align-items :center
                                                  :justify-content :center
                                                  :padding "0 10px"
                                                  :height "100%"}
                                                 checkbox-container-style
                                                 (when check-disabled?
                                                   {:cursor :default
                                                    :opacity 0.6}))
                                   :theme (style/dark-style (merge {:background-color (if checked?
                                                                                        (util/hex->rgba (style/dark-color :primary :darker-1) 0.2)
                                                                                        (util/hex->rgba (style/dark-color :surface :darker-1) 0.2))
                                                                    :border-right (str "1px solid " (style/dark-color :surface :base))}
                                                                   dark-checkbox-container-style))
                                   :hover-style (merge {:background-color
                                                        (if checked?
                                                          (style/color :green :shade-4)
                                                          (style/color :gray :shade-11))}
                                                       checkbox-container-hover-style)
                                   :dark-hover-style (merge {:background-color
                                                             (if checked?
                                                               (style/dark-color :primary :lighter-1)
                                                               (style/dark-color :surface :lighter-1))}
                                                            dark-checkbox-container-hover-style)
                                   :on-click (when-not check-disabled?
                                               #(do
                                                  (on-check item)
                                                  (util/stop-event %)))}
                 [icon/material-icon {:icon-type (if checked?
                                                   :check_box
                                                   :check_box_outline_blank)
                                      :style (merge {:color (if checked?
                                                              (style/color :green :base)
                                                              (style/color :gray :base))}
                                                    checkbox-style)
                                      :dark-style (merge {:color (if checked?
                                                                   (style/dark-color :primary :darker-1)
                                                                   (style/dark-color :on-surface :base))}
                                                         dark-checkbox-style)}]])
              (into [:div {:style {:flex 1
                                   :display :flex
                                   :align-items :center
                                   :padding (condp = size
                                              :medium "5px 6px 6px 5px"
                                              "12px 15px 12px 10px")}}]
                    [content])
              (when expandable?
                [theme/theme
                 icon/material-icon {:icon-type (if children?
                                                  (if expanded?
                                                    :expand_more
                                                    :chevron_right)
                                                  :remove)
                                     :style {:color (style/color :gray :base)
                                             :margin "0 10px"}
                                     :theme (style/dark-style {:color (style/dark-color :on-surface :base)})}])]
             (when (and actions (or @main-hover?* action-hover? force-show-actions?))
               (into [theme/theme
                      interactive :div {:style {:background-color (style/color :white :base)
                                                :border-radius (condp = size
                                                                 :medium 25
                                                                 36)
                                                :padding "3px 0"
                                                :display :flex
                                                :align-items :center
                                                :position :absolute
                                                :top "50%"
                                                :right 35
                                                :transform "translateY(-50%)"}
                                        :theme (style/dark-style {:background-color (style/dark-color :surface :base)})
                                        :hover?* main-hover?*}]
                     (map-indexed (fn [idx action]
                                    ^{:key (str idx (pr-str action))}
                                    (if (map? action)
                                      [tree-action (assoc action :parent-hover?* main-hover?*
                                                                 :size size
                                                                 :hover?* (try
                                                                            (nth @action-hovers* idx)
                                                                            (catch :default _
                                                                              main-hover?*)))]
                                      action))
                                  (filter identity actions))))]
            (when (and children? expanded?)
              (into [:div {:style {:background-color (or child-cont-bg-color
                                                         (condp = (rem level 3)
                                                           0 (style/color :gray :base)
                                                           1 (style/color :gray :shade-1)
                                                           2 (style/color :gray :shade-3)))
                                   :border-bottom-left-radius (if child? 0 3)
                                   :border-bottom-right-radius (if child? 0 3)
                                   :border-left (when child-cont-border-color
                                                  (str "8px solid " child-cont-border-color))
                                   :padding (if child-cont-border-color
                                              "8px 0"
                                              (str "8px "
                                                   (if child-cont-bg-color "3px" 0)
                                                   " 8px 8px"))}}]
                    (map-indexed (fn [idx {:keys [style] :as c}]
                                   [tree-item (assoc c :style (merge {:margin-top (if (= idx 0)
                                                                                    0
                                                                                    8)}
                                                                     style)
                                                       :size size
                                                       :show-actions-toggled? show-actions-toggled?
                                                       :rev-idx (- (count children) idx)
                                                       :level (inc level))])
                                 children)))]))})))

(defn tree [_]
  (let [show-actions?* (re/ratom true)]
    (fn [{:keys [children style size show-actions-toggle?]}]
      (let [show-actions-toggled? @show-actions?*]
        (into [:div {:style style}
               (when show-actions-toggle?
                 [:div {:style {:display :flex
                                :justify-content :flex-end
                                :margin-bottom 5
                                :width "100%"}}
                  [a/a {:on-click #(reset! show-actions?* (not @show-actions?*))
                        :style (condp = size
                                 :medium {:font-size 13}
                                 {})}
                   "Toggle Actions"]])]
              (map-indexed (fn [idx {:keys [style] :as c}]
                             [tree-item (assoc c :show-actions-toggled? show-actions-toggled?
                                                 :level 0
                                                 :rev-idx (- (count children) idx)
                                                 :size size
                                                 :style (merge {:margin-top (if (= idx 0) 0 10)}
                                                               style))])
                           children))))))

(defn mini-tree-item [{:keys [checkable? checked? expandable? expanded? children on-click on-check style inner-style
                              content-container-style dark-content-container-style content radio? disabled?
                              hover-style? on-mouse-over]
                       :or {hover-style? true}
                       :as item}]
  (let [children? (some-> children (count) (pos?))
        on-click (when-not disabled? (or on-click (when checkable? on-check)))]
    [:div {:style (merge {:cursor (if on-click :pointer :default)
                          :transition "0.2s"}
                         style)
           :on-click #(do
                        (when on-click (on-click item))
                        (util/stop-event %))}
     [:div {:style (merge {:flex 1
                           :display :flex
                           :position :relative}
                          inner-style)}
      [theme/theme
       interactive
       :div {:style (cond-> {:color (style/color :gray :base)
                             :font-size 13
                             :font-weight style/font-medium-weight
                             :flex 1
                             :display :flex
                             :align-items :center
                             :transition "0.2s"}
                            on-click (assoc :cursor :pointer)
                            disabled? (assoc :cursor :not-allowed
                                             :opacity 0.5)
                            (not expandable?) (assoc :padding-left 2)
                            content-container-style (merge content-container-style))
             :hover-style (when (and on-click hover-style?)
                            {:background-color (util/hex->rgba (style/color :green :base) 0.1)})
             :theme (style/dark-prop {:style (merge {:color (style/dark-color :on-surface :base)}
                                                    dark-content-container-style)
                                      :hover-style (when (and on-click hover-style?)
                                                     {:background-color (style/dark-color :primary :lighter-1)
                                                      :color (style/dark-color :on-primary :base)})})
             :on-mouse-over on-mouse-over}
       (when (and expandable? children?)
         [theme/theme
          icon/material-icon {:icon-type :arrow-drop-down
                              :style {:color (style/color :gray :base)
                                      :font-size 17
                                      :transform (when-not expanded? "rotate(-90deg)")}
                              :theme (style/dark-style {:color (style/dark-color :on-surface :base)})}])
       (when checkable?
         (if radio?
           [radio {:selected? checked?
                   :on-click #(do
                                (when on-check (on-check item))
                                (util/stop-event %))
                   :support-deselect? (and (not disabled?) on-check)
                   :disabled? disabled?
                   :disabled-style {:opacity 1}
                   :style {:font-size 17
                           :margin-right 3}}]
           [checkbox {:checked? checked?
                      :on-click #(do
                                   (when on-check (on-check item))
                                   (util/stop-event %))
                      :disabled? disabled?
                      :disabled-style {:opacity 1}
                      :style {:font-size 17
                              :margin-right 3}}]))
       (into [:div {:style {:color :inherit
                            :flex 1
                            :display :flex
                            :align-items :center
                            :padding "3px 0"
                            :user-select :none}}]
             [content])]]
     (when (and children? expanded? (not disabled?))
       (into [:div {:style {:padding-left 12}}]
             (map (fn [c] [mini-tree-item c])
                  children)))]))

(defn mini-tree [{:keys [children style]}]
  (into [:div {:style style}]
        (map (fn [c] [mini-tree-item c])
             children)))
